import { SUPPORT_API_HOST, SUPPORT_WS_EVENT, SUPPORT_WS_RETRY_MAX_MS, SUPPORT_WS_RETRY_MS } from '@/constants/support'

/**
 * The support service's WebSocket, which is how a reply actually arrives: posting to
 * `/ai-assist` only starts the answer, and an agent's message has no request to ride back on.
 * Without this the Chat tab would show the dealer's own words and nothing else.
 *
 * The protocol is not documented anywhere — it is read off the deployed widget bundle:
 * authenticate with the session token, subscribe per conversation, then frames arrive by
 * `type`. Treat it as reverse-engineered: every frame is optional and every field is checked
 * before use, because a shape change on their side must degrade to "no live update", never
 * to a thrown error inside a socket callback.
 *
 * One socket per tab, module-level — the Help Hub mounts once, and a second socket would
 * double every message.
 */

let socket = null
let authed = false
// Set by `close()` so the reconnect timer doesn't resurrect a socket we deliberately dropped.
let closing = false
let retryTimer = null
let retryDelay = SUPPORT_WS_RETRY_MS
let getToken = () => null

// Conversations to re-subscribe to after a reconnect: the server forgets them when the
// socket drops, so the set is the client's memory of what it was watching.
const subscribed = new Set()
const messageHandlers = new Set()
const statusHandlers = new Set()
const conversationHandlers = new Set()
const progressHandlers = new Set()

// `https://host/api` → `wss://host/api/ws`, matching how the widget builds it.
const socketUrl = () => {
  const base = (SUPPORT_API_HOST ?? '').replace(/\/+$/, '')

  if (/^https?:\/\//.test(base)) {
    const url = new URL(base)
    return `${url.protocol === 'https:' ? 'wss:' : 'ws:'}//${url.host}${url.pathname.replace(/\/$/, '')}/ws`
  }

  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}${base}/ws`
}

const send = (payload) => {
  if (socket?.readyState !== WebSocket.OPEN) return
  try {
    socket.send(JSON.stringify(payload))
  } catch {
    // A send that fails means the socket is already gone; `onclose` handles reconnecting.
  }
}

const handleFrame = (raw) => {
  let frame = null
  try {
    frame = JSON.parse(raw)
  } catch {
    return
  }
  if (!frame?.type) return

  if (frame.type === SUPPORT_WS_EVENT.AUTH_OK) {
    authed = true
    retryDelay = SUPPORT_WS_RETRY_MS
    subscribed.forEach((id) => send({ type: 'subscribe', conversationId: id }))
    return
  }

  if (frame.type === SUPPORT_WS_EVENT.NEW_MESSAGE && frame.message) {
    // The agents' side writes private notes into the same stream; they are not for the dealer.
    if (String(frame.message.isInternal) === 'true') return
    messageHandlers.forEach((handler) => handler(frame.message))
    return
  }

  if (frame.type === SUPPORT_WS_EVENT.CONVERSATION_UPDATED && frame.conversation) {
    conversationHandlers.forEach((handler) => handler(frame.conversation))
    statusHandlers.forEach((handler) => handler(frame.conversation.id, frame.conversation.status))
    return
  }

  if (frame.type === SUPPORT_WS_EVENT.STATUS_CHANGE && frame.conversationId) {
    statusHandlers.forEach((handler) => handler(frame.conversationId, frame.status))
    return
  }

  if (frame.type === SUPPORT_WS_EVENT.AI_PROGRESS && frame.conversationId) {
    progressHandlers.forEach((handler) => handler(frame.conversationId, frame.step, frame.detail))
  }

  // `typing` stays unhandled: it names a human agent who is writing, and nothing in the
  // panel distinguishes that from the AI working, which `ai_progress` already shows.
}

const scheduleRetry = () => {
  if (closing || retryTimer) return
  retryTimer = setTimeout(() => {
    retryTimer = null
    retryDelay = Math.min(retryDelay * 2, SUPPORT_WS_RETRY_MAX_MS)
    connect()
  }, retryDelay)
}

export const connect = () => {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return

  const token = getToken()
  if (!token) return

  closing = false
  authed = false

  try {
    socket = new WebSocket(socketUrl())
  } catch {
    socket = null
    scheduleRetry()
    return
  }

  socket.onopen = () => {
    clearTimeout(retryTimer)
    retryTimer = null
    send({ type: 'auth', clientType: 'customer', token: getToken() })
  }

  socket.onmessage = (event) => handleFrame(event.data)

  socket.onclose = () => {
    socket = null
    authed = false
    scheduleRetry()
  }

  // An error is always followed by a close, which is where the retry lives.
  socket.onerror = () => socket?.close()
}

/** The store hands over how to read the current session token, so the socket holds no copy. */
export const initSupportSocket = (tokenGetter) => {
  getToken = tokenGetter
}

export const subscribeToConversation = (id) => {
  if (!id) return
  subscribed.add(id)
  if (authed) send({ type: 'subscribe', conversationId: id })
  else connect()
}

export const unsubscribeFromConversation = (id) => {
  if (!id) return
  subscribed.delete(id)
  send({ type: 'unsubscribe', conversationId: id })
}

export const onSupportMessage = (handler) => {
  messageHandlers.add(handler)
  return () => messageHandlers.delete(handler)
}

export const onSupportStatus = (handler) => {
  statusHandlers.add(handler)
  return () => statusHandlers.delete(handler)
}

// The whole record as it changes: a handoff an agent makes from their side reaches the thread this way.
export const onSupportConversation = (handler) => {
  conversationHandlers.add(handler)
  return () => conversationHandlers.delete(handler)
}

export const onSupportProgress = (handler) => {
  progressHandlers.add(handler)
  return () => progressHandlers.delete(handler)
}

export const closeSupportSocket = () => {
  closing = true
  authed = false
  clearTimeout(retryTimer)
  retryTimer = null
  retryDelay = SUPPORT_WS_RETRY_MS
  subscribed.clear()
  socket?.close()
  socket = null
}
